import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import RoleTabs from '@/components/feature/RoleTabs';
import PlanCard from '@/components/feature/PlanCard';
import { getPlans, ROLE_META, ROLE_ORDER } from '@/lib/subscription';
import type { Plan } from '@/lib/subscription';

const pricingHero =
  'https://readdy.ai/api/search-image?query=Modern%20subscription%20and%20pricing%20dashboard%20concept%2C%20abstract%20financial%20growth%20visual%2C%20clean%20minimal%20design%20with%20warm%20golden%20and%20amber%20tones%2C%20soft%20gradient%20background%2C%20professional%20editorial%20illustration%2C%20high%20detail%2C%20harmonious%20composition&width=1600&height=700&seq=pricing-hero-01&orientation=landscape';

function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [activeRole, setActiveRole] = useState('business');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getPlans();
        if (mounted) setPlans(data);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const visiblePlans = plans.filter((p) => p.role === activeRole);
  const activeMeta = ROLE_META[activeRole];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={pricingHero} alt="Tarifs WorldApeiron" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">
              Tarifs & Abonnements
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
              Choisissez l'offre qui vous correspond
            </h1>
            <p className="text-base md:text-lg text-foreground-400 max-w-2xl leading-relaxed">
              Des abonnements mensuels adaptés à chaque profil : entreprise, freelance,
              particulier ou formateur. Chaque offre a sa capacité — publications, produits,
              stockage et accès aux modules.
            </p>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="pricing-plans" className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col items-center mb-10">
            <RoleTabs activeRole={activeRole} onChange={setActiveRole} />
            <p className="text-sm text-foreground-400 mt-4 text-center">
              {activeMeta
                ? `Offres pour les ${activeMeta.label.toLowerCase()}s`
                : 'Sélectionnez un profil'}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-accent-400" />
            </div>
          ) : (
            <div
              className={`grid grid-cols-1 gap-6 max-w-4xl mx-auto ${
                visiblePlans.length > 1 ? 'md:grid-cols-2' : 'md:max-w-md'
              }`}
            >
              {visiblePlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  actionLabel="Créer un compte"
                  onAction={() => navigate('/auth/register')}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Overview of all profiles */}
      <section className="py-14 md:py-20 bg-background-100 border-t border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Un abonnement pour chaque profil
            </h2>
            <p className="text-foreground-400 max-w-2xl mx-auto">
              Retrouvez ci-dessous l'aperçu des prix de départ pour chaque catégorie.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {ROLE_ORDER.map((role) => {
              const meta = ROLE_META[role];
              if (!meta) return null;
              const rolePlans = plans.filter((p) => p.role === role);
              const minPrice = rolePlans.length
                ? Math.min(...rolePlans.map((p) => p.price_monthly))
                : null;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => {
                    setActiveRole(role);
                    document.getElementById('pricing-plans')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-400/40 transition-all duration-300 text-left"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/10 mb-3">
                    <i className={`${meta.icon} text-xl text-accent-400`} />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-50 mb-1">
                    {meta.label}
                  </h3>
                  <p className="text-sm text-foreground-400">
                    {minPrice !== null ? `dès ${minPrice.toLocaleString('fr-FR')} FCFA/mois` : '—'}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center p-8 md:p-12 rounded-2xl bg-gradient-to-br from-primary-500/15 to-accent-500/10 border border-background-200/70">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Prêt à rejoindre WorldApeiron ?
            </h2>
            <p className="text-foreground-400 mb-6 max-w-xl mx-auto">
              Créez votre compte et choisissez l'offre qui correspond à votre activité. Le
              paiement en ligne sera bientôt disponible.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/auth/register')}
                className="px-8 py-3 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap text-sm"
              >
                Créer mon compte
              </button>
              <button
                type="button"
                onClick={() => navigate('/auth/login')}
                className="px-8 py-3 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm"
              >
                Se connecter
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PricingPage;